/** How the client obtains bearer tokens. Hosts implement this once (session storage, ticket auth, keychain ...). */
export interface TokenProvider {
  /** A valid access token, or null when signed out. */
  getToken(): Promise<string | null> | string | null;
  /** Called once on 401 before giving up; returns the new token or null. */
  refresh?(): Promise<string | null>;
  /** The server rejected the credentials for good (or the refresh failed). */
  onAuthExpired?(reason: 'unauthorized' | 'refresh-failed' | 'server'): void;
  /** The current refresh token (sent on logout); null when unknown. */
  refreshToken?(): string | null;
}

function base64UrlDecode(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  if (typeof atob === 'function') {
    const bin = atob(padded);
    try {
      return decodeURIComponent(
        Array.from(bin, (c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')
      );
    } catch {
      return bin;
    }
  }
  const B = (globalThis as { Buffer?: { from(s: string, enc: string): { toString(enc: string): string } } })
    .Buffer;
  return B ? B.from(padded, 'base64').toString('utf8') : '';
}

/** Decode the payload of a JWT without verifying it (the server verifies; the client only reads claims). */
export function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    return JSON.parse(base64UrlDecode(parts[1])) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** `exp` claim in milliseconds since the epoch, or 0 when the token cannot be read. */
export function jwtExpiryMs(token: string): number {
  const exp = decodeJwt(token)?.exp;
  return typeof exp === 'number' ? exp * 1000 : 0;
}

function coerceStrings(raw: unknown): string[] {
  if (raw == null) return [];
  if (typeof raw === 'string') return [raw];
  if (Array.isArray(raw)) return raw.filter((r): r is string => typeof r === 'string');
  return [];
}

/** AUTH scopes from the `scope` claim (space separated string or array). */
export function decodeTokenScopes(token: string): string[] {
  const payload = decodeJwt(token);
  const raw = payload?.['scope'] ?? payload?.['scp'];
  if (typeof raw === 'string') return raw.split(/[\s,]+/).filter(Boolean);
  return coerceStrings(raw);
}

/**
 * True when `granted` satisfies `required`. A granted `svc:*` matches every `svc:…` scope (`orders:*` grants all of
 * Orders); anything else must match exactly. An empty `required` is always satisfied.
 */
export function scopeMatches(granted: string, required: string): boolean {
  if (granted === required) return true;
  if (granted.endsWith(':*')) return required.startsWith(granted.slice(0, -1));
  return false;
}

/** True when any of `scopes` satisfies `required` (see `scopeMatches`). */
export function hasScope(
  scopes: readonly string[] | null | undefined,
  required: string | undefined
): boolean {
  if (!required) return true;
  return (scopes ?? []).some((g) => scopeMatches(g, required));
}
